/**
 * Which customer this browser session belongs to.
 *
 * Historically the answer was compiled in: one webpack build per tenant, each with that
 * customer's name, API URL and colours baked in by REACT_APP_* substitution, published to its own
 * S3 prefix. That is why there are 27 builds of the same application.
 *
 * Here the answer arrives at runtime instead, from the front door at sign-in, and is kept in
 * localStorage so a refresh or a deep link resolves it again without a round trip.
 *
 * WHY LOCALSTORAGE AND NOT REACT STATE: roughly a hundred modules read COMPANY_TITLE, COMPANY_DNS
 * and friends as plain `export const`, evaluated once when the bundle loads. Those are not
 * reactive and making them reactive would mean touching every one of those files. Reading the
 * tenant synchronously at module-eval time keeps every existing call site correct as written; the
 * only rule is that the tenant must be known BEFORE the bundle evaluates, which is why
 * `applyTenant` reloads the page rather than mutating anything in place.
 *
 * Nothing here is a security boundary. The tenant recorded in this browser only decides which API
 * the app talks to and how it is branded; the API still authorises every request against the JWT
 * that tenant issued, so editing this by hand gets you a differently-branded login page and
 * nothing more.
 */

export interface TenantConfig {
  id: string
  slug: string
  title: string
  /** Short brand name. The UI's `companyName`, distinct from the long/legal `title`. */
  name: string
  /** Vanity domain. Email addresses like closing@<dns> are built from it. */
  dns?: string | null
  apiUrl: string
  logoUrl?: string | null
  primaryColor?: string | null
  secondaryColor?: string | null
}

const STORAGE_KEY = 'nexys.tenant'

/**
 * The hostname the stored tenant was resolved FROM, when it was the URL that identified it.
 *
 * Recorded rather than kept in memory because `tenantFromHost` has to survive a reload. On every
 * visit after the first the tenant comes back from storage and nothing calls `setTenant` with
 * `fromHost` again, so an in-memory flag reads false and the sign-out that landing on /login
 * dispatches deletes a tenant the hostname still identifies - taking the SSO buttons with it,
 * since without a tenant the page believes it is at the anonymous front door.
 *
 * A hostname and not a bare boolean: the claim being stored is "this URL identifies this
 * customer", which is true at longhorn.nexys.com and false at app.nexys.com, so the same browser
 * visiting both must not carry one host's answer to the other.
 */
const STORAGE_HOST_KEY = 'nexys.tenant.host'

/** The host this page is being served from, lowercased, '' when there is no window. */
const currentHost = typeof window === 'undefined' ? '' : (window.location.hostname || '').toLowerCase()

function readStoredHost(): string {
  try {
    return (window.localStorage.getItem(STORAGE_HOST_KEY) || '').toLowerCase()
  } catch {
    // Storage unavailable: fall back to "not resolved from the host", which is what this flag
    // meant before it was persisted at all.
    return ''
  }
}

function readStoredTenant(): TenantConfig | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // apiUrl is the only field the app cannot function without.
    return parsed && typeof parsed.apiUrl === 'string' && parsed.apiUrl ? (parsed as TenantConfig) : null
  } catch {
    // Private browsing, disabled storage, or a corrupt entry. Falling back to the build-time
    // environment is always safe: that is exactly how the per-tenant builds behave today.
    return null
  }
}

/**
 * Resolved once, at module evaluation, on purpose. See the note above about `export const`.
 * It is mutable only so that `setTenant` can redirect API traffic mid-sign-in, before the
 * reload that settles the display constants.
 */
let activeTenant: TenantConfig | null = typeof window === 'undefined' ? null : readStoredTenant()

/**
 * What was stored when the bundle loaded, which is what the module-level display constants were
 * evaluated against. Learning a tenant from the hostname mid-load changes `activeTenant` but
 * not those constants, so this is the value sign-in must compare against to decide whether a
 * restart is still needed.
 */
const tenantAtLoad: TenantConfig | null = activeTenant

/**
 * Whether the active tenant came from the hostname rather than from signing in.
 *
 * It decides whether signing out may clear it. On a customer's own hostname the tenant is a
 * property of the URL, not of the session: clearing it there wipes what the front door just
 * resolved, and since landing on the sign-in page dispatches a sign-out, the two fight - the
 * tenant is stored and immediately deleted, over and over.
 *
 * Restored from storage, not reset per load: the hostname answers the same way on every visit,
 * and only the first one goes through `setTenant(..., true)`.
 */
let tenantFromHost = Boolean(activeTenant) && !!currentHost && readStoredHost() === currentHost

/** The tenant this build was compiled for, when there is no runtime tenant. */
const buildTimeApiUrl = process.env.REACT_APP_API_URL || ''

/**
 * Branding resolved from the hostname, before anyone has signed in.
 *
 * A customer reaching their own subdomain or vanity domain should see their own login page,
 * not a neutral one. The host already identifies them, so the front door can answer with the
 * branding without any credential being offered. Held separately from the tenant record
 * because it is exactly that: presentation, resolved pre-auth, carrying no API URL.
 */
export interface TenantBranding {
  title?: string | null
  logoUrl?: string | null
  primaryColor?: string | null
  secondaryColor?: string | null
}

let hostBranding: TenantBranding | null = null

export function getHostBranding(): TenantBranding | null {
  return hostBranding
}

export function setHostBranding(branding: TenantBranding | null): void {
  hostBranding = branding
  notify()
}

const listeners = new Set<() => void>()

export function getTenant(): TenantConfig | null {
  return activeTenant
}

/** The tenant the display constants were evaluated against. See `tenantAtLoad`. */
export function getTenantAtLoad(): TenantConfig | null {
  return tenantAtLoad
}

/**
 * Subscribe to tenant changes, for the few values read at render time rather than at module
 * load. See `refreshTenant` for why only some of them can work this way.
 */
export function subscribeTenant(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function notify() {
  listeners.forEach((l) => l())
}

/**
 * The API origin for the CURRENT request, read live rather than captured at module load.
 *
 * Routing has to follow the tenant the moment sign-in resolves one, because two-factor
 * verification happens before the reload: send2faCode and verify2faCode must reach the same
 * backend that issued the challenge, not whichever one this bundle was built against.
 */
export function getApiUrl(): string {
  return activeTenant?.apiUrl || buildTimeApiUrl
}

/**
 * Records the tenant and points API traffic at it immediately. Does NOT reload: the caller
 * finishes the sign-in first, then calls `reloadForTenant` so nothing half-written is lost.
 */
export function setTenant(tenant: TenantConfig, fromHost = false): void {
  activeTenant = tenant
  if (fromHost) tenantFromHost = true
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tenant))
    // Only ever widened, never cleared here: once this hostname has identified a customer, a
    // later sign-in on the same host does not make the URL stop identifying them.
    if (fromHost && currentHost) window.localStorage.setItem(STORAGE_HOST_KEY, currentHost)
  } catch {
    // Storage unavailable: the session still works, but only until the next reload.
  }
  notify()
}

/**
 * Re-reads the current tenant from the front door and applies any change.
 *
 * The record is captured at sign-in, so without this a tenant that changes its logo or name is
 * not seen until each user signs in again.
 *
 * Only values read at RENDER time pick the change up immediately - the logo and the display
 * name. COMPANY_TITLE and the rest are plain module constants evaluated once when the bundle
 * loaded, so those still settle on the next load. Deliberately not forcing a reload here:
 * refreshing the page under someone mid-task to update a logo is worse than the stale logo.
 */
export async function refreshTenant(): Promise<void> {
  const current = activeTenant
  if (!current?.id || !HAS_FRONT_DOOR) return
  try {
    const response = await fetch(`${GATEWAY_URL}/tenants/${encodeURIComponent(current.id)}`)
    if (!response.ok) return
    const fresh = (await response.json()) as TenantConfig
    if (!fresh?.apiUrl) return
    if (JSON.stringify(fresh) === JSON.stringify(current)) return
    setTenant(fresh)
  } catch {
    // Branding is cosmetic; a failed refresh must never affect the session.
  }
}

/**
 * Restarts the app so every module-level constant is re-evaluated against the tenant.
 *
 * Call this only once the session is safely persisted. Without the reload the app keeps running
 * with constants resolved for whatever tenant was active when the bundle first evaluated, which
 * is the quiet version of this bug: the right data, under the wrong company's name.
 */
export function reloadForTenant(redirectTo = '/'): void {
  window.location.replace(redirectTo)
}

/**
 * Reads a tenant field, falling back to the value compiled into this build.
 *
 * The fallback is what makes this change safe to ship before the migration finishes: a
 * per-tenant build with no stored tenant behaves exactly as it does today, so the same bundle
 * serves both the old per-customer deployments and app.nexys.com.
 */
export function tenantValue<K extends keyof TenantConfig>(key: K, buildTimeFallback?: string): string {
  const fromTenant = activeTenant?.[key]
  if (typeof fromTenant === 'string' && fromTenant) return fromTenant
  return buildTimeFallback ?? ''
}

/** Clears the tenant on sign-out so the next user reaches the neutral front door. */
export function clearTenant(): void {
  // A tenant the hostname identifies outlives any session at that hostname.
  if (tenantFromHost) return
  activeTenant = null
  notify()
  try {
    window.localStorage.removeItem(STORAGE_KEY)
    window.localStorage.removeItem(STORAGE_HOST_KEY)
  } catch {
    /* nothing to clear */
  }
}

/**
 * The front door's own origin.
 *
 * Unset means "no front door": the app talks straight to the API it was built for, which is the
 * current per-tenant deployment. That is what keeps this a strangler fig rather than a fork.
 */
const rawGateway = (process.env.REACT_APP_GATEWAY_URL || '').trim()

/**
 * Origin of the front door, or '' when it is served from this same origin.
 *
 * Set it to '/' (or 'same-origin') when the API is reachable under the same host as the app,
 * which is how the shared deployment works: one CDN distribution serves the bundle and proxies
 * the front-door paths to the load balancer. Requests then go to '/auth/login' rather than an
 * absolute URL, so the SAME build runs unchanged on the CDN hostname, on app.nexys.com, and on
 * any tenant vanity domain. Baking a hostname in at build time would put us straight back to
 * rebuilding per environment, which is the thing this change exists to remove.
 */
export const GATEWAY_URL = rawGateway === '/' || rawGateway === 'same-origin' ? '' : rawGateway.replace(/\/+$/, '')

/** Unset means no front door: talk straight to the API this build was compiled for. */
export const HAS_FRONT_DOOR = rawGateway !== ''

/**
 * Applies the browser-chrome parts of the tenant's branding: tab title and favicon.
 *
 * These live in index.html, which is substituted at build time, so a single shared bundle cannot
 * carry them per customer. Setting them here before first paint keeps one build serving every
 * customer without each one losing its own tab icon and title.
 *
 * No-op when no tenant is active, which is exactly the legacy per-tenant build: index.html
 * already has the right values compiled in and must be left alone.
 */
export function applyTenantChrome(): void {
  const tenant = activeTenant
  if (!tenant) return

  if (tenant.title) document.title = tenant.title

  if (tenant.logoUrl) {
    const existing = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
    const link = existing ?? document.createElement('link')
    link.rel = 'icon'
    link.href = tenant.logoUrl
    if (!existing) document.head.appendChild(link)
  }
}
